/**
 * Deferred deep-link infrastructure.
 *
 * Notifications and shared URLs sometimes target a protected route while the
 * user is logged out. The protected route guards redirect them to the login
 * page, losing the original URL. This helper persists the intended URL in
 * sessionStorage so the login page can restore it after a successful auth.
 *
 * Per-portal keys prevent admin/HR/finance collisions when multiple desks are open.
 */

export type Portal = "admin" | "hr" | "finance";

const KEYS: Record<Portal, string> = {
  admin: "pending_deep_link_admin",
  hr: "pending_deep_link_hr",
  finance: "pending_deep_link_finance",
};

const TTL_MS = 10 * 60 * 1000; // 10 minutes

interface Envelope {
  url: string;
  ts: number;
}

const isOnFinanceSubdomain = (): boolean => {
  try {
    return typeof window !== "undefined" && window.location.hostname === "finance.hibane.ai";
  } catch {
    return false;
  }
};

const portalPrefixes = (portal: Portal): string[] => {
  if (portal === "admin") return ["/admin"];
  if (portal === "hr") return ["/hr/"];
  // Finance: legacy /finance/* AND root paths on finance subdomain.
  return isOnFinanceSubdomain() ? ["/finance/", "/dashboard", "/login"] : ["/finance/"];
};

const loginPath = (portal: Portal): string => {
  if (portal === "admin") return "/admin/login";
  if (portal === "hr") return "/hr/login";
  return isOnFinanceSubdomain() ? "/login" : "/finance/login";
};

/**
 * Returns true if the URL is "interesting" enough to remember.
 * Skips bare login redirects so we don't loop back to /login post-auth.
 */
const startsWithAnyPrefix = (portal: Portal, url: string): boolean =>
  portalPrefixes(portal).some((p) => url === p.replace(/\/$/, "") || url.startsWith(p));

const isWorthSaving = (portal: Portal, url: string): boolean => {
  // Absolute URLs (cross-origin SW dispatches) are always worth saving — the
  // post-auth handler will window.location.assign them.
  if (/^https?:\/\//i.test(url)) return true;
  if (!startsWithAnyPrefix(portal, url)) return false;
  if (url === loginPath(portal) || url.startsWith(`${loginPath(portal)}?`)) return false;

  const hasQuery = url.includes("?");
  if (hasQuery) return true;

  // Deeper than the bare landing path
  if (portal === "admin") {
    return url !== "/admin" && url !== "/admin/";
  }
  if (portal === "hr") {
    return url !== "/hr" && url !== "/hr/" && url !== "/hr/dashboard" && url !== "/hr/dashboard/";
  }
  // finance
  return (
    url !== "/finance" &&
    url !== "/finance/" &&
    url !== "/finance/dashboard" &&
    url !== "/finance/dashboard/" &&
    url !== "/dashboard" &&
    url !== "/dashboard/"
  );
};

const safeStorage = (): Storage | null => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

export function savePendingDeepLink(portal: Portal, url: string): void {
  const storage = safeStorage();
  if (!storage) return;
  if (!url || !isWorthSaving(portal, url)) return;

  const env: Envelope = { url, ts: Date.now() };
  try {
    storage.setItem(KEYS[portal], JSON.stringify(env));
  } catch {
    // ignore quota / privacy errors
  }
}

function read(portal: Portal): string | null {
  const storage = safeStorage();
  if (!storage) return null;

  const raw = storage.getItem(KEYS[portal]);
  if (!raw) return null;

  try {
    const env = JSON.parse(raw) as Envelope;
    if (!env?.url || typeof env.ts !== "number") {
      storage.removeItem(KEYS[portal]);
      return null;
    }
    if (Date.now() - env.ts > TTL_MS) {
      storage.removeItem(KEYS[portal]);
      return null;
    }
    const isAbsolute = /^https?:\/\//i.test(env.url);
    if (!isAbsolute) {
      if (!startsWithAnyPrefix(portal, env.url)) {
        storage.removeItem(KEYS[portal]);
        return null;
      }
      if (env.url === loginPath(portal) || env.url.startsWith(`${loginPath(portal)}?`)) {
        storage.removeItem(KEYS[portal]);
        return null;
      }
    }
    return env.url;
  } catch {
    try {
      storage.removeItem(KEYS[portal]);
    } catch { /* noop */ }
    return null;
  }
}

export function peekPendingDeepLink(portal: Portal): string | null {
  return read(portal);
}

export function consumePendingDeepLink(portal: Portal): string | null {
  const url = read(portal);
  const storage = safeStorage();
  if (storage) {
    try { storage.removeItem(KEYS[portal]); } catch { /* noop */ }
  }
  return url;
}

export function clearPendingDeepLink(portal: Portal): void {
  const storage = safeStorage();
  if (!storage) return;
  try { storage.removeItem(KEYS[portal]); } catch { /* noop */ }
}
